<template>
  <div class="vu-file-upload">
    <div v-if="type == 'button'">
      <v-btn
        v-show="!loading"
        icon
        @click="triggerClick"
      >
        <v-icon>file_upload</v-icon>
      </v-btn>
      <v-progress-circular
        v-show="loading"
        :value="progress"
        :indeterminate="loading && (progress == 0 || progress == 100)"
        color="primary"
        :size="36"
        :rotate="-90"
      />
    </div>

    <div
      v-else
      ref="box"
      style="position: relative"
    >
      <v-card :flat="flat">
        <v-img
          :src="dataUrl"
          contain
          class="grey lighten-2"
          :class="{ 'disabled-gradient': disabled }"
          :height="150"
        >
          <v-layout
            fill-height
            align-center
            justify-center
            ma-0
          >
            <span v-if="disabled">{{ disabled }}</span>
            <v-icon v-show="!loading && !imgSelected">
              {{ type === 'image' ? 'image' : 'insert_drive_file' }}
            </v-icon>
            <v-progress-circular
              v-show="loading"
              :value="progress"
              :indeterminate="loading && (progress == 0 || progress == 100)"
              color="accent"
              :size="70"
              :rotate="-90"
            />
          </v-layout>
        </v-img>

        <v-card-text>
          {{ placeholder }}<br>
          <small><i>Supported: {{ accept }}</i></small>
        </v-card-text>
      </v-card>

      <label
        :for="inputId"
        class="img-inputer__label"
      />
    </div>

    <input
      :id="inputId"
      ref="inputer"
      type="file"
      :name="name"
      :accept="accept"
      :capture="capture ? 'video' : null"
      :disabled="disabled"
      class="img-inputer__inputer"
      @change="handleFileChange"
    >

    <transition name="vip-fade">
      <div
        v-if="errText.length"
        class="img-inputer__err"
      >
        {{ errText }}
      </div>
    </transition>
  </div>
</template>

<script>
import { uploadFile } from '@/storage'

export default {
  props: {
    accept: {
      default: 'image/*,video/*',
      type: String
    },
    capture: {
      default: false,
      type: Boolean
    },
    disabled: {
      type: [Boolean, String],
      default: false
    },
    // Path in FB Storage where to upload the file
    firebaseStorage: {
      default: null,
      type: String
    },
    // Firestore ref to the doc where to save the URL of uploaded file
    firestoreRef: {
      type: Object,
      default: null
    },
    // Field inside firestoreRef where to write the URL
    firestoreField: {
      type: String,
      default: null
    },
    flat: {
      type: Boolean,
      default: false
    },
    id: {
      default: '',
      type: String
    },
    imgSrc: {
      default: '',
      type: String
    },
    maxSize: {
      default: 5120,
      type: Number
    },
    name: {
      type: String,
      default: 'file'
    },
    storagePath: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'image',
      validator: function (val) {
        return ['image', 'file', 'button'].includes(val)
      }
    },
    placeholder: {
      default: 'Click or Drag file here',
      type: String
    },
    size: {
      default: '',
      type: String
    }
  },
  data () {
    return {
      inputId: '',
      file: [],
      dataUrl: '',
      fileName: '',
      loading: false,
      progress: 0,
      errText: ''
    }
  },
  computed: {
    imgSelected () {
      return !!this.dataUrl || !!this.fileName
    },
    sizeHumanRead () {
      let rst = 0
      if (this.maxSize < 1024) {
        rst = this.maxSize + 'K'
      } else {
        rst = (this.maxSize / 1024).toFixed(this.maxSize % 1024 > 0 ? 2 : 0) + 'M'
      }
      return rst
    }
  },
  watch: {
    imgSrc (newval) {
      this.dataUrl = newval

      if (!newval) {
        this.file = []
        this.errText = ''
        this.fileName = ''
      }
    }
  },
  mounted () {
    this.inputId = this.id || this.gengerateID()
    if (this.imgSrc) {
      this.dataUrl = this.imgSrc
    }

    if (this.type === 'image') {
      ['dragleave', 'drop', 'dragenter', 'dragover'].forEach(e => {
        this.preventDefaultEvent(e)
      })
      this.addDropSupport()
    }
  },
  methods: {
    preventDefaultEvent (eventName) {
      document.addEventListener(eventName, function (e) {
        e.preventDefault()
      }, false)
    },
    triggerClick () {
      var event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      })
      this.$refs.inputer.dispatchEvent(event)
    },
    addDropSupport () {
      let BOX = this.$refs.box

      BOX.addEventListener('drop', (e) => {
        e.preventDefault()
        this.errText = ''
        let fileList = e.dataTransfer.files

        if (fileList.length === 0) {
          return false
        }

        if (fileList.length > 1) {
          this.errText = '暂不支持多文件'
          return false
        }

        this.handleFileChange(fileList)
      })
    },
    gengerateID () {
      let nonstr = Math.random().toString(36).substring(3, 8)
      if (!document.getElementById(nonstr)) {
        return nonstr
      } else {
        return this.gengerateID()
      }
    },
    handleFileChange (e) {
      if (typeof e.target === 'undefined') this.file = e[0]
      else this.file = e.target.files[0]

      this.errText = ''
      let size = Math.floor(this.file.size / 1024)
      if (size > this.maxSize) {
        this.errText = `File size bigger than ${this.sizeHumanRead}`
        return false
      }

      if (this.firebaseStorage || this.storagePath) {
        this.loading = true

        let filename
        if (this.firebaseStorage) {
          filename = `${this.firebaseStorage}.${this.file.name.split('.').pop()}`
        } else {
          filename = `${this.storagePath.replace(/\/\s*$/, '')}/${this.file.name}`
        }

        uploadFile(this.file, filename, (progress, state) => {
          if (progress === 100) {
            this.progress = 0
          } else {
            this.progress = progress
          }
        })
          .then(url => {
            if (this.firestoreRef && this.firestoreField) {
              const data = {}
              data[this.firestoreField] = url

              let ref = this.firestoreRef
              if (ref.add) {
                ref = ref.add(data)
              } else {
                ref = ref.update(data)
              }

              ref
                .then(() => {
                  this.loading = false
                  this.$emit('uploaded', null, this.file, url)
                })
                .catch(err => {
                  this.loading = false
                  this.$emit('uploaded', err)
                })
            } else {
              this.$emit('uploaded', null, this.file, url)
            }
          })
          .catch(error => {
            this.loading = false
            this.$emit('uploaded', error)
          })
      } else {
        this.$emit('input', this.file)
        this.$emit('change', this.file, this.file.name)
      }

      this.imgPreview(this.file)

      this.fileName = this.file.name

      this.resetInput()
    },
    imgPreview (file) {
      let self = this

      if (!file || !window.FileReader) return

      if (/^image/.test(file.type)) {
        let reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onloadend = function () {
          self.dataUrl = this.result
        }
      }
    },
    resetInput () {
      let input = document.getElementById(this.inputId)
      let form = document.createElement('form')

      document.body.appendChild(form)

      let parentNode = input.parentNode
      // 判断input 是否为最后一个节点
      let isLastNode = parentNode.lastChild === input
      let nextSibling

      // 如果后面还有节点，则记录下一个node，做位置标志
      // 如果本身已经是最后一个节点，则直接通过parentNode appendChild即可
      if (!isLastNode) {
        nextSibling = input.nextSibling
      }

      form.appendChild(input)
      form.reset()

      isLastNode
        ? parentNode.appendChild(input)
        : parentNode.insertBefore(input, nextSibling)

      document.body.removeChild(form)
    }
  }
}
</script>

<style lang="scss">
.list__tile__action .progress-circular {
  margin-right: -8px;
  margin-top: 8px;
}

.img-inputer {
  position: relative;
  display: inline-block;
  width: 260px;
  height: 150px;
  border-radius: 5px;
  background: #f0f0f0;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .117647), 0 1px 4px rgba(0, 0, 0, .117647);
  transition: .3s cubic-bezier(0.4, 0, 0.2, 1);

  &__label {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    cursor: pointer;
    margin-bottom: 0;
  }

  &__inputer {
    position: absolute;
    left: -9999px;
  }

  &__err {
    color: #e55;
    font-size: 12px;
    position: absolute;
    bottom: -28px;
    width: 100%;
  }
}

.vu-file-upload {
  .disabled-gradient {
    background-image: repeating-linear-gradient(-45deg,
                        rgba(255,0,0,.25),
                        rgba(255,0,0,.25) 5px,
                        rgba(0,0,255,.25) 5px,
                        rgba(0,0,255,.25) 10px
                      );
  }
}

.vip-fade {
  &-enter-active, &-leave-active {
    transition: .15s cubic-bezier(0.4, 0.0, 0.2, 1);
    will-change: trnsform;
  }
  &-enter, &-leave-active {
    opacity: 0;
    transform: translateX(30%);
  }
}
</style>
